import { useContext } from 'react';
import * as R from 'ramda';

import { Droppable } from '@atom/components/common/dragAndDrop';
import { Progress } from '@atom/mui';
import {
  getCurrentPageModule,
  isCurrentPageStandaloneModule,
} from '@atom/selectors/formModuleSelectors';
import { AttributeGroupItem } from '@atom/types/form';
import { AttributesType } from '@atom/types/inventory';
import { SchemaType } from '@atom/types/schema';

import FormModule from './formModules/FormModule';
import FieldContainer from './FieldContainer';
import FormBuilderContext from './FormBuilderContext';

import classes from './formBuilder.module.css';

export interface Props {
  loading: boolean;
  rootSchema: SchemaType;
  deleteField: (id: string) => void;
  duplicateField: (id: string) => void;
  updateFormTemplate: (id: string, data: Object) => void;
  updateAttributeGroup: (
    fieldId: string,
    schemaId: string,
    addGroup: boolean,
    attributeGroup: AttributeGroupItem,
  ) => void;
  updateAttribute: (
    fieldId: string,
    schemaId: string,
    addAttribute: boolean,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => void;
  addAllAttributes: (
    fieldId: string,
    schemaId: string,
    attributeGroupName: string,
    attributes: AttributesType[],
  ) => void;
  removeAssetSchema: (fieldId: string, schemaId: string) => void;
  updateCiFormInspection: (fieldId: string, inspectionId: string) => void;
}

const DROPPABLE_ID = 'fields';

const FormBuilderCanvas = ({
  loading,
  deleteField,
  duplicateField,
  updateFormTemplate,
  rootSchema,
  updateAttributeGroup,
  updateAttribute,
  addAllAttributes,
  removeAssetSchema,
  updateCiFormInspection,
}: Props) => {
  const { formTemplate, selectedPage } = useContext(FormBuilderContext);

  const renderFields = (): any[] => {
    const isStandaloneModule = isCurrentPageStandaloneModule(
      formTemplate,
      selectedPage,
    );

    if (R.isEmpty(formTemplate.pages)) {
      return [];
    }

    const matchedPage = formTemplate.pages[selectedPage];
    const page = matchedPage ? matchedPage : { order: [], fields: {} };

    return page.order.map((key: string, index: number) => {
      const field = page.fields[key];

      return !isStandaloneModule ? (
        <FieldContainer
          field={field}
          rootSchema={rootSchema}
          id={key}
          index={index}
          key={key}
          updateFormTemplate={updateFormTemplate}
          updateAttributeGroup={updateAttributeGroup}
          addAllAttributes={addAllAttributes}
          removeAssetSchema={removeAssetSchema}
          updateAttribute={updateAttribute}
          deleteField={deleteField}
          duplicateField={duplicateField}
        />
      ) : (
        <FormModule
          key={index}
          field={field}
          fieldId={key}
          updateCiFormInspection={updateCiFormInspection}
        />
      );
    });
  };

  const moduleType = getCurrentPageModule(formTemplate, selectedPage);

  const content = loading ? (
    <div className={classes.spinnerContainer}>
      <Progress />
    </div>
  ) : (
    renderFields()
  );

  return (
    <div className={classes.formEditCanvas}>
      <Droppable
        droppableId={DROPPABLE_ID}
        className={classes.formEditPaper}
        style={
          moduleType
            ? { borderTop: `3px solid ${moduleType.color}` }
            : undefined
        }
      >
        {content}
      </Droppable>
    </div>
  );
};

export default FormBuilderCanvas;
